/**
 * @fileoverview AR-924 — Step 1 of "Upload for class": drop zone + tip, staged list and the Cancel / Next footer.
 */

import { Anchor, Badge, Button, Group, Paper, Stack, Text } from '@mantine/core'
import { IconArrowRight, IconLink } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { MAX_FILE_SIZE_MB } from '../teacherUpload'
import { ClassDropzone } from './ClassDropzone'
import { StagedItemRow } from './StagedItemRow'
import type { StagedSummary } from './stagedItems'
import type { UploadForClassState } from './useUploadForClass'

const BUTTON_ICON_SIZE = 16

interface AddFilesStepProps {
  readonly state: UploadForClassState
  readonly studentNamesOf: (studentIds: readonly string[]) => string | null
  readonly onAddLink: () => void
  readonly onCancel: () => void
}

type Translate = (key: string, options?: object) => string

function rejectedText(summary: StagedSummary, tr: Translate): string {
  const reason = summary.firstFailReason === 'too_large'
    ? tr('failed_too_large', { mb: MAX_FILE_SIZE_MB })
    : tr('failed_type')
  if (summary.failed === 1) return tr('summary_failed_one', { reason })
  return tr('summary_failed', { count: summary.failed })
}

function SummaryLine({ summary }: { summary: StagedSummary }) {
  const { t } = useTranslation()
  const tr: Translate = (key, options) => t(`task_tracker.upload_for_class.${key}`, options)

  const parts: React.ReactNode[] = []
  if (summary.uploaded) parts.push(tr('summary_uploaded', { count: summary.uploaded }))
  if (summary.ready) parts.push(tr('summary_ready', { count: summary.ready }))
  if (summary.uploading) parts.push(tr('summary_uploading', { count: summary.uploading }))
  if (summary.failed) {
    parts.push(<Text key="failed" span color="red" weight={600}>{rejectedText(summary, tr)}</Text>)
  }
  if (summary.uploadFailed) {
    parts.push(
      <Text key="upload-failed" span color="red" weight={600}>
        {tr('summary_upload_failed', { count: summary.uploadFailed })}
      </Text>,
    )
  }
  return (
    <Text size="sm" color="dimmed">
      {parts.map((part, index) => <span key={index}>{index > 0 && ' · '}{part}</span>)}
    </Text>
  )
}

export function AddFilesStep({ state, studentNamesOf, onAddLink, onCancel }: AddFilesStepProps) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.upload_for_class.${key}`, options)

  return (
    <Stack spacing="md">
      <ClassDropzone disabled={state.submitting} onFiles={state.addFiles} />

      <Group position="apart">
        <Group spacing="xs">
          <Text size="md" weight={700}>{tr('files_and_links')}</Text>
          <Badge variant="filled" color="gray" radius="xl" size="sm">{state.items.length}</Badge>
        </Group>
        <Group spacing="md">
          <Button variant="outline" size="xs" leftIcon={<IconLink size={BUTTON_ICON_SIZE} />} onClick={onAddLink}>
            {tr('add_link')}
          </Button>
          {state.items.length > 0 && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              color="dimmed"
              onClick={state.clear}
              disabled={state.submitting}
            >
              {tr('clear_all')}
            </Anchor>
          )}
        </Group>
      </Group>

      {state.items.length > 0 && (
        <Paper withBorder radius="md" sx={{ overflow: 'hidden' }}>
          {state.items.map(item => (
            <StagedItemRow
              key={item.id}
              item={item}
              studentName={studentNamesOf(item.studentIds)}
              disabled={state.submitting}
              onRemove={state.remove}
            />
          ))}
        </Paper>
      )}

      <Group position="apart" pt="xs">
        <SummaryLine summary={state.summary} />
        <Group spacing="xs">
          <Button variant="default" onClick={onCancel}>{tr('cancel')}</Button>
          <Button
            rightIcon={<IconArrowRight size={BUTTON_ICON_SIZE} />}
            disabled={!state.canProceed}
            onClick={() => state.setStep('match')}
          >
            {tr('next')}
          </Button>
        </Group>
      </Group>
    </Stack>
  )
}
